"use client";

import React, { useEffect, Suspense, lazy, useRef } from 'react';
import Header from '@/components/sections/Header';
import SearchBar from '@/components/sections/SearchBar';
import { useScroll } from '@/context/ScrollContext';
import useVisibleSection from '@/hooks/useVisibleSection';

// Lazy load sections for better performance
const ShareSection = lazy(() => import('@/components/sections/ShareSection'));
const JoinTelegram = lazy(() => import('@/components/sections/JoinTelegram'));
const Features = lazy(() => import('@/components/sections/Features'));
const AboutSection = lazy(() => import('@/components/sections/AboutSection'));
const Faq = lazy(() => import('@/components/sections/Faq'));

export default function Home() {
    const { registerRef, setVisibleSection } = useScroll();

    // Individual refs are stable across renders (useRef returns same object)
    const headerRef = useRef<HTMLElement>(null);
    const searchRef = useRef<HTMLElement>(null);
    const featuresRef = useRef<HTMLElement>(null);
    const aboutRef = useRef<HTMLElement>(null);
    const faqRef = useRef<HTMLElement>(null);

    // Track current visible section for navigation highlighting
    const visibleSection = useVisibleSection();

    useEffect(() => {
        setVisibleSection(visibleSection);
    }, [visibleSection, setVisibleSection]);

    // Register section references with global ScrollProvider
    useEffect(() => {
        registerRef('header', headerRef);
        registerRef('search', searchRef);
        registerRef('features', featuresRef);
        registerRef('about', aboutRef);
        registerRef('faq', faqRef);
    }, [registerRef]);

    return (
        <>
            <Header ref={headerRef} id="header" />
            <main>
                <Suspense fallback={null}>
                    <SearchBar ref={searchRef} id="search" />
                    <ShareSection />
                    <JoinTelegram />
                    <Features ref={featuresRef} id="features"/>
                    <AboutSection ref={aboutRef} id="about" />
                    <Faq ref={faqRef} id="faq" />
                </Suspense>
            </main>
        </>
    );
}
